/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import $ from 'jquery';
import 'perfect-scrollbar';
import React, { ComponentType, RefObject } from 'react';
import TableComponent from './TableComponent';
import { checkUpdateVisualData, checkViewPortDisplay } from './BodyContainerComponentHandlers';
import {
    IBodyContainerProps,
    ITableComponentProps,
    ITableVisualizationDynamicProperties
} from '../types/tableVisualizationTypes';

interface IBodyContainerComponentHandlers {
    checkViewPortDisplay(viewPortRef: RefObject<any>, props: IBodyContainerProps): void,
    checkUpdateVisualData(viewPortRef: RefObject<any>, props: IBodyContainerProps): void
}

const DEFAULT_CONTENT_HEIGHT = 1200;

export function BodyContainerComponentHOC(Table: ComponentType<ITableComponentProps>, handlers: IBodyContainerComponentHandlers)
    : ComponentType<IBodyContainerProps> {

    return class BodyContainerComponent extends React.Component<IBodyContainerProps> {

        viewPortRef: RefObject<any>;

        scrollInitialized: boolean;

        constructor(props: IBodyContainerProps) {
            super(props);
            this.onScroll = this.onScroll.bind(this);
            this.viewPortRef = React.createRef();
            this.scrollInitialized = false;
            this.handleCheckViewPortDisplay = this.handleCheckViewPortDisplay.bind(this);
        }

        componentDidMount(): void {
            if (this.props.rows.length) {
                this.initializeScrollbars();
            }

            this.checkRenderingSize();
        }

        componentDidUpdate(): void {
            if (this.props.rows.length) {
                this.initializeScrollbars();
            } else {
                this.removeScrollbars();
            }

            handlers.checkUpdateVisualData(this.viewPortRef, this.props);
            this.checkRenderingSize();
        }

        componentWillUnmount() {
            this.removeScrollbars();
        }

        onScroll(event: any) {
            this.props.onScrollCallback(event);
            this.handleCheckViewPortDisplay(event);
        }

        initializeScrollbars() {
            if (this.viewPortRef.current) {
                if (this.props.dynamicProperties.contentHeight === -1) {
                    this.viewPortRef.current.scrollTop = 0;
                }

                if (this.scrollInitialized) {
                    // @ts-ignore
                    $(this.viewPortRef.current).perfectScrollbar('update');
                } else {
                    // @ts-ignore
                    $(this.viewPortRef.current).perfectScrollbar();
                    this.viewPortRef.current.addEventListener('scroll', this.onScroll);
                    this.scrollInitialized = true;
                }
            }
        }

        removeScrollbars() {
            if (this.viewPortRef.current && this.scrollInitialized) {
                this.viewPortRef.current.removeEventListener('scroll', this.onScroll);
                // @ts-ignore
                $(this.viewPortRef.current).perfectScrollbar('destroy');
                this.scrollInitialized = false;
            }
        }

        // eslint-disable-next-line react/sort-comp
        handleCheckViewPortDisplay(event: any) {
            if (event.target.classList.contains('ps-active-y') || event.target.classList.contains('ps-y')) {
                handlers.checkViewPortDisplay(this.viewPortRef, this.props);
            }
        }

        checkRenderingSize() {
            if (this.viewPortRef.current) {
                const {
                    dynamicProperties: {
                        minRowHeight, rowsCountToRender
                    },
                    onUpdateDynamicProperties
                } = this.props;

                const viewPortHeight = this.viewPortRef.current.offsetHeight;
                const currentRowsCountToRender = Math.ceil((viewPortHeight * 2.3) / minRowHeight);

                if (rowsCountToRender < currentRowsCountToRender) {
                    onUpdateDynamicProperties({
                        rowsCountToRender: currentRowsCountToRender
                    } as ITableVisualizationDynamicProperties)
                }
            }
        }

        render() {
            const {
                getRef, rows, staticProperties, dynamicProperties, linkOptions, linkToRefObjectPairs
            } = this.props;
            const {
                bottomHeight, contentHeight, offsetTop, headerHeight, tableContainerHeight
            } = dynamicProperties;

            const showTotalsContainer = staticProperties.columns.reduce((memo, column) => (memo || !!column.showSummary), !!staticProperties.showTotals);
            const actualContentHeight = contentHeight === -1 && staticProperties.showDetails ? DEFAULT_CONTENT_HEIGHT : contentHeight;
            const compensatorHeight = showTotalsContainer ? bottomHeight : 0;

            const bodyContainerStyles: any = {};
            if (tableContainerHeight - headerHeight > 0) {
                bodyContainerStyles.maxHeight = `${tableContainerHeight - headerHeight}px`;
            }

            getRef(this.viewPortRef);

            return (
                <div
                    className="jr-mDatatable-bodyContainer jr jr-jTableBodyContainer"
                    ref={this.viewPortRef}
                    style={bodyContainerStyles}
                >
                    <div className="jr-jBodyBackground" style={{ height: actualContentHeight }} />
                    <Table
                        rows={rows}
                        offsetTop={offsetTop}
                        linkToRefObjectPairs={linkToRefObjectPairs}
                        linkOptions={linkOptions}
                    />
                    <div
                        className="jr-uCompensator"
                        style={{
                            height: compensatorHeight
                        }}
                    />
                </div>
            );
        }
    }
}

export const BodyContainerComponent = BodyContainerComponentHOC(
    TableComponent,
    {
        checkViewPortDisplay,
        checkUpdateVisualData
    }
);
